import { Box, Button, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { UseQueryResult } from "react-query";

import HarvestAPI from "../api/Harvest.api";
import { Harvest, HarvestInput, Lot } from "../types";
import Autosave from "./Autosave";
import LotSearch from "./LotSearch";
import SearchForSublotGroup from "./SearchForSublotGroup";
import SplitSublotModal from "./SplitSublotModal";

type Props = {
  harvest: Harvest;
  refetchHarvest: () => Promise<UseQueryResult>;
};

export default function SelectLotOrSublot({ harvest, refetchHarvest }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [lot, setLot] = React.useState<Lot | undefined>(harvest?.lot);

  const [openSplitModal, setOpenSplitModal] = React.useState<boolean>(false);
  const [openSearchSublotModal, setOpenSearchSublotModal] =
    React.useState<boolean>(false);

  const handleOpenSplitModal = () => {
    if (lot) {
      setOpenSplitModal(true);
    } else {
      enqueueSnackbar(
        "Not able to split a harvest into sublots if a lot has not been choosen",
        {
          variant: "error",
        },
      );
    }
  };

  const handleOpenSearchSublotModal = () => {
    if (lot) {
      setOpenSearchSublotModal(true);
    } else {
      enqueueSnackbar(
        "Not able to search for a sublot if a lot has not been choosen",
        {
          variant: "error",
        },
      );
    }
  };

  const harvestInput: HarvestInput = {
    id: harvest.id,
    lotableId: lot?.id,
    lotableType: lot?.lotableType,
  };

  const { mutateAsync: autoSaveMutateAsync } = HarvestAPI.useUpdateAuto();

  const patchApiCall = async (input: HarvestInput) => {
    await autoSaveMutateAsync(input);
    await refetchHarvest();
  };

  return (
    <React.Fragment>
      <Autosave
        data={harvestInput}
        patchApiCall={patchApiCall}
        showPopup={true}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row", lg: "row" },
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <LotSearch lot={lot} setLot={setLot} />
        </Box>
        {harvest.lotable && (
          <Box sx={{ flex: 1 }}>
            <TextField
              label={"Number of Acres"}
              value={harvest.lotable.numOfAcres || ""}
              fullWidth
              disabled
            />
          </Box>
        )}
        {harvest?.lotableType === "Lot" && (
          <Button onClick={handleOpenSplitModal} variant="outlined">
            Split into Sublots
          </Button>
        )}

        <Button onClick={handleOpenSearchSublotModal} variant="outlined">
          Select Sublot Group
        </Button>
      </Box>

      <SplitSublotModal
        open={openSplitModal}
        setOpen={setOpenSplitModal}
        harvest={harvest}
        refetchHarvest={refetchHarvest}
      />
      <SearchForSublotGroup
        lot={lot}
        setLot={setLot}
        open={openSearchSublotModal}
        setOpen={setOpenSearchSublotModal}
        harvest={harvest}
        refetchHarvest={refetchHarvest}
      />
    </React.Fragment>
  );
}
